/* eslint-disable camelcase */

import dbee from '@os/dbee';
import { fetchGeonames, fetchGeoLocation } from './location-client';

const localStorage = dbee('location-model');
const STORAGE_KEY = 'locations';
const EXPIRY_KEY = 'expiry';
const STORAGE_VERSION = '2';

export default { get, set, init };

function versionControl(rawStorage) {
	if (rawStorage && +rawStorage.version !== +STORAGE_VERSION) {
		localStorage.clear();
		rawStorage = undefined;
	}
	return rawStorage;
}

function get(defaultLocation) {
	return Promise.resolve(processFromCache(
		versionControl(localStorage.get(STORAGE_KEY)),
		defaultLocation
	));
}

function set(locations) {
	locations.version = STORAGE_VERSION;
	localStorage.set(STORAGE_KEY, processToCache(locations));
}

function init(defaultLocation) {
	return get(defaultLocation)
		.then((cached) => {
			let state;

			if (cached && cached.favorites) {
				console.info('LocationModel: Using locations from cache:', cached); // eslint-disable-line no-console
				state = Object.assign({}, state, cached);
			}

			const isPrimaryLocationExpired = isLocationExpired(cached && cached.primary);
			isPrimaryLocationExpired && localStorage.remove(EXPIRY_KEY);

			return {
				state,
				isPrimaryLocationExpired
			};
		})
		.then(({ state = {}, isPrimaryLocationExpired }) => (
			isPrimaryLocationExpired ? (
				fetchGeoLocation(defaultLocation)
					.then((location) => {
						state.primary = location;

						state.current = state.current || location;

						// If the location is the primary location and no expiration is set, set a new expiration.
						const expiry = localStorage.get(EXPIRY_KEY);
						if (!expiry && location.expiry) {
							localStorage.set(EXPIRY_KEY, location.expiry);
						}

						return state;
					})
			) : (
				state
			)
		));
}

function isLocationExpired(location) {
	return !location || (location.expiry || 0) <= Date.now();
}

function processToCache(allLocations = {}) {
	// remove favorites that are also recent searches.
	allLocations.recent = allLocations.recent && allLocations.recent
		.filter((recentLoc) => (
			allLocations.favorites.map( favorite => favorite.id ).indexOf(recentLoc.id) < 0
		))
		.slice(0, 5);

	// process locations into geonamesId
	return Object.keys(allLocations).reduce((acc, key) => {
		if (key === 'version') { acc[key] = allLocations[key]; return acc; }

		const locations = allLocations[key];

		acc[key] = Array.isArray(locations)
			? locations.map(({ geoname_id }) => +geoname_id)
			: +locations.geoname_id;

		return acc;
	}, {});
}

function processFromCache(cached, defaultLocation) {
	// flatten all geonames into one request.
	let cacheKeys = cached && Object.keys(cached);
	if (!cacheKeys || !cacheKeys.length) { return; }

	cacheKeys.forEach((key) => { if (!cached[key]) delete cached[key];});
	delete cached.version;
	cacheKeys = Object.keys(cached);

	// Flatten and batch geonames into one request.
	const allGeonameIds = cacheKeys.reduce((acc, key) => acc.concat(cached[key]), []);

	return fetchGeonames(allGeonameIds, defaultLocation)
		.then((locations) => {
			// Re-shape the locations back to { primary, current, favorites, recent }
			let locationObj = cacheKeys.reduce((acc, key) => {
				const geonameIds = cached[key];
				acc[key] = !Array.isArray(geonameIds)
					? locations.filter(({ geoname_id }) => +geonameIds === +geoname_id)[0]
					: locations.filter(({ geoname_id }) => geonameIds.some((geonameId) => +geonameId === +geoname_id));
				return acc;
			}, {});
			const expiry = localStorage.get(EXPIRY_KEY);
			if (locationObj.primary && expiry) { locationObj.primary.expiry = expiry; }
			// Ensure favorites exists, even if empty
			if (!locationObj.favorites) locationObj.favorites = [];
			return locationObj;
		})
		.catch(err => console.error('location-model processFromCache error: ', err));
}

/* eslint-enable camelcase */
